import { Calendar } from '@/components/calendar/Calendar';
import { MonthPicker } from '@/components/shared/date/MonthPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat, getMonthBounds } from '@/helpers/dateHelper';
import { useEmployeeSchedule } from '@/services/employee/schedule/useEmployeeSchedule';
import { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { strings } from '@/services/translation/strings';
import { TeamMemberCalendarContent } from './TeamMemberCalendarContent';
import { useTeamMemberCalendarEvents } from './useTeamMemberCalendarEvents';

interface TeamMemberCalendarProps {
  selectedDate: Date;
  setSelectedDate: (newDate: Date) => void;
}

export const calendarSectionId = 'calendarSectionId';

export const TeamMemberCalendar: FC<TeamMemberCalendarProps> = ({ selectedDate, setSelectedDate }) => {
  const { userId } = useParams<'userId'>();

  if (!userId) {
    throw Error('User id should be provided in url params');
  }

  const [startDate, endDate] = useMemo(() => {
    return getMonthBounds(selectedDate);
  }, [selectedDate]);

  const data = useEmployeeSchedule(userId, startDate, endDate);

  const navigate = useNavigate();

  const [sheetDate, setSheetDate] = useState<Date | null>(null);
  const onDayClick = useCallback((date: Date) => setSheetDate(date), []);
  const closeSheet = () => setSheetDate(null);

  const events = useTeamMemberCalendarEvents(data.schedule, onDayClick);

  const dayEvent = events.find(e => e.date.toISOString() === sheetDate?.toISOString());
  const pendingRequest = dayEvent?.contextObject?.pendingAvailabilityRequest;

  const handleViewDetails = () => {
    // Same destination the day click used before
    navigate(`/team-member/${userId}/calendar-details/${dateToApiFormat(sheetDate!)}`);
    closeSheet();
  };

  const handleSetAvailability = () => {
    navigate(`/team-member/${userId}/calendar-details/${dateToApiFormat(sheetDate!)}/edit-availability`);
    closeSheet();
  };

  const handleAddLeave = () => {
    navigate(`/team-member/${userId}/calendar-details/${dateToApiFormat(sheetDate!)}/add-leave`);
    closeSheet();
  };

  const handleViewPendingRequest = () => {
    if (!pendingRequest) return;

    const pendingException = pendingRequest.exceptions?.[0];
    if (!pendingException) return;

    if (pendingException.leaveType?.id) {
      navigate(`/team-member/${userId}/leave-request/${pendingRequest.id}`);
    } else {
      navigate(`/team-member/${userId}/availability-exception/${pendingRequest.id}`);
    }

    closeSheet();
  };

  return (
    <section id={calendarSectionId} className="space-y-5">
      <MonthPicker value={selectedDate} onChange={setSelectedDate} />
      <Suspense fallback={<TableLoader />}>
        <Calendar
          monthToShow={startDate}
          events={events}
          ContentComponent={TeamMemberCalendarContent}
          options={{ minimumCellHeight: 115, hideOverflow: true }}
        />
      </Suspense>

      {sheetDate && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={closeSheet}>
          <div
            className="absolute bottom-0 left-0 right-0 z-50 bg-white divide-y-2 rounded-t-2xl overflow-hidden text-center"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={handleViewDetails} className="w-full py-4 font-semibold">
              {strings.timeshift.teamMember.calendar.viewDetails}
            </button>
            {pendingRequest && (
              <button onClick={handleViewPendingRequest} className="w-full py-4 font-semibold">
                {strings.timeshift.teamMember.calendar.viewPendingRequest}
              </button>
            )}
            <button onClick={handleSetAvailability} className="w-full py-4 font-semibold">
              {strings.timeshift.teamMember.calendar.setAvailability}
            </button>
            <button onClick={handleAddLeave} className="w-full py-4 font-semibold">
              {strings.timeshift.teamMember.calendar.bookLeave}
            </button>
            <button onClick={closeSheet} className="w-full py-4 font-semibold">
              {strings.timeshift.teamMember.calendar.cancel}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
